import { Product } from "../../types/productTypes";
import api from "../../config/Api";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

const API_URL = "/products";

export const fetchProductById = createAsyncThunk<Product, number>(
    "products/fetchProductById",
    async (productId, { rejectWithValue }) => {
        try {
            const response = await api.get(`${API_URL}/${productId}`);
            const data = response.data;
            return data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const searchProduct = createAsyncThunk<Product[], string>(
    "products/searchProduct",
    async (query: string, { rejectWithValue }) => {
        try {
            const response = await api.get(`${API_URL}/search`, {
                params: { query },
            });
            const data = response.data;
            //console.log("search product data: ", data);
            return data;
        } catch (error: any) {
            //console.log("error: " + error);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const fetchAllProducts = createAsyncThunk<any, Record<string, any> | undefined>(
    "products/fetchAllProducts",
    async (params = {}, { rejectWithValue }) => {
        try {
            const response = await api.get(`${API_URL}`, {
                params: {
                    ...params,
                    // Handle fallback default page name seamlessly
                    pageNumber: params?.pageNumber ?? 0,
                },
            });
            const data = response.data;
            //console.log("All product data: ", data);
            return data;
        } catch (error: any) {
            //console.log("error: " + error);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

interface ProductState {
    product: Product | null;
    products: Product[];
    totalPages: number;
    loading: boolean;
    error: string | null | undefined | any;
    searchProduct: Product[];
}

const initialState: ProductState = {
    product: null,
    products: [],
    totalPages: 1,
    loading: false,
    error: null,
    searchProduct: [],
};

const productSlice = createSlice({
    name: "products",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(fetchProductById.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(fetchProductById.fulfilled, (state, action) => {
            state.loading = false;
            state.product = action.payload;
        });
        builder.addCase(fetchProductById.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        builder.addCase(fetchAllProducts.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(fetchAllProducts.fulfilled, (state, action) => {
            state.loading = false;
            
            // FIXED: Added fallback logic in case the response structure varies
            if (action.payload && action.payload.content) {
                state.products = action.payload.content;
                state.totalPages = action.payload.totalPages || 1;
            } else if (Array.isArray(action.payload)) {
                state.products = action.payload;
            } else {
                state.products = [];
            }
        });
        builder.addCase(fetchAllProducts.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        builder.addCase(searchProduct.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(searchProduct.fulfilled, (state, action) => {
            state.loading = false;
            state.searchProduct = Array.isArray(action.payload) ? action.payload : [];
        });
        builder.addCase(searchProduct.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });
    },
});

export default productSlice.reducer;